import { ChevronDown, ChevronRight } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { AuditChanges, auditChanges } from "@/components/audit-changes"
import { PageHeader } from "@/components/layout"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AuditEntry } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"

export function ActivityPage() {
  const t = useT()
  const { locale } = useLocale()
  const [rows, setRows] = React.useState<AuditEntry[]>([])
  const [expanded, setExpanded] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    api
      .auditMe(200, 0)
      .then((page) => setRows(page.items))
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : t("activity.toasts.loadFailed")),
      )
      .finally(() => setLoading(false))
  }, [t])

  return (
    <>
      <PageHeader title={t("activity.title")} description={t("activity.description")} />
      <div className="space-y-4 p-6">
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("activity.empty")}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">{t("activity.columns.time")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("activity.columns.action")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("activity.columns.target")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("activity.columns.ip")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const changes = auditChanges(row)
                  const isOpen = expanded === row.id
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={changes ? "hover:bg-accent/40 cursor-pointer border-t" : "border-t"}
                        onClick={changes ? () => setExpanded(isOpen ? null : row.id) : undefined}
                      >
                        <td className="text-muted-foreground px-4 py-2">
                          {formatDateTime(row.created_at, locale)}
                        </td>
                        <td className="px-4 py-2 font-medium">
                          <span className="flex items-center gap-1.5">
                            {changes ? (
                              isOpen ? (
                                <ChevronDown className="size-3.5 shrink-0" />
                              ) : (
                                <ChevronRight className="size-3.5 shrink-0" />
                              )
                            ) : (
                              <span className="inline-block w-3.5" />
                            )}
                            {row.action}
                          </span>
                        </td>
                        <td className="px-4 py-2">{row.target_label ?? row.target_type ?? "—"}</td>
                        <td className="text-muted-foreground px-4 py-2">{row.ip_address ?? "—"}</td>
                      </tr>
                      {changes && isOpen && (
                        <tr className="bg-muted/30 border-t">
                          <td colSpan={4} className="px-4 py-3 pl-11">
                            <AuditChanges changes={changes} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
